<script lang="ts" setup>
  import { onMounted, onUnmounted, ref } from "vue";
  import { CardApiService, type CardPostPayload } from "@/services/cardApiService";
  import { useCardsStore } from "@/stores/cards";
  import type { FormEventPayload } from "@/models/card";
  import { useAppStore } from "@/stores/app";
  import { useDebounceFn } from "@vueuse/core";
  import { useSessionStore } from '@/stores/session';
  import * as amplitude from "@amplitude/analytics-browser";
  import { useRouter } from "vue-router";
  import cardSuccessImage from '@/assets/images/card-success.png';
  import { useSubscriptionFlowStore } from "@/stores/subscriptionFlow";
  import { MembershipTierPlanFrequency } from '@/models/membershipTierPlan';
  import { useI18n } from 'vue-i18n';
  import type { ApiError } from '@/utils/apiClient'

  const props = defineProps<{
    membershipTierId?: number;
    membershipTierPlanId?: number;
    maxRoundUp?: number | null;
    membershipImportMode?: boolean;
    height?: number;
  }>();

  const emit = defineEmits<{
    linking: [];
    success: [data?: FormEventPayload];
    error: [error: unknown];
  }>();

  const sessionStore = useSessionStore();
  const appStore = useAppStore();
  const cardsStore = useCardsStore();
  const subscriptionFlowStore = useSubscriptionFlowStore();
  const router = useRouter();
  const { t } = useI18n();

  const loading = ref(true);

  // Dynamic form management
  const bgElementId = 'modal-link-card-bg';
  const formElementId = 'modal-link-card-form';

  async function loadPlatformForm() {
    if (document.getElementById('change-it-link-card-form-script')) {
      return;
    }
    try {
      const response = await CardApiService.getCardHostedForm();

      // create and load script
      const mainElement = document.getElementById(bgElementId);
      const script = document.createElement('script');
      script.id = 'change-it-link-card-form-script';
      script.type = 'text/javascript';
      script.textContent = response.data?.scriptContent ?? '';
      script.setAttribute('data-container-id', formElementId);
      script.setAttribute('data-success-event', 'change-it-link-card-form-success');
      script.setAttribute('data-error-event', 'change-it-link-card-form-error');
      mainElement?.parentNode?.insertBefore(script, mainElement.nextSibling);
    } catch (error) {
      amplitude.track('Payment Failed', {
        "Error Type": (error as Error).message ?? error,
      });
      console.log(error);
    } finally {
      setTimeout(() => {
        loading.value = false;
      }, 2000);
    }
  }

  // Script cb management
  const cardLinking = ref<boolean>(false);
  const cardError = ref<boolean>(false);
  const cardErrorMessage = ref<string>('');
  const cardSuccess = ref<boolean>(false);

  const linkSuccess = useDebounceFn(async function (event: CustomEvent) {
    cardLinking.value = true;
    emit('linking');
    const data = event.detail as FormEventPayload;
    try {
      if (!props.membershipImportMode) {
        const payload: CardPostPayload = {
          name: data.name ?? data.system,
          system: data.system,
          currencyCode: appStore.client!.currencyCode,
          platformData: data.platformData,
        };
        if (props.membershipTierId) {
          payload.membershipTierId = props.membershipTierId;
        }
        if (props.membershipTierPlanId) {
          payload.membershipTierPlanId = props.membershipTierPlanId;
        }
        if (props.maxRoundUp && props.maxRoundUp > 0) {
          payload.maxRoundUp = props.maxRoundUp;
        }

        // TODO extract this to an external event
        await cardsStore.createCard(payload);

        // Membership tier and sweepstake
        const lotteryNumbers = sessionStore.lotteryNumbers;
        await sessionStore.refreshUser();
        if (lotteryNumbers !== sessionStore.lotteryNumbers && sessionStore.lotteryNumbers.length > 0) {
          appStore.showSweepStakesReminder = true;
        }

        if (props.membershipTierId) {
          // Update
          const selectedTier = appStore.membershipTiers.find((e) => e.id === props.membershipTierId);
          const selectedTierPlan = selectedTier?.membershipTierPlans?.find((e) => e.id === props.membershipTierPlanId);

          amplitude.track('Membership Activated', {
            "Tier Name": selectedTier?.name ?? '',
            "Tier Price": (selectedTier?.price ?? 0) < 0 ? 'Roundup' : (selectedTierPlan?.price ?? ''),
            "Tier Cycle": selectedTierPlan?.frequency ?? MembershipTierPlanFrequency.MONTH,
          });
        }
      }

      cardSuccess.value = true;
      setTimeout(async () => {
        emit('success', data);
      }, 2000);
    } catch (e) {
      const error = e as ApiError;
      console.error(error);
      if(error.messageCode === 'olive_cc_003') {
        // Card already exists for another program
        cardErrorMessage.value = t(error.messageCode, {email: appStore.client!.supportEmail}) ?? null;
      } else {
        cardErrorMessage.value = error.message;
      }

      cardError.value = true;
      emit('error', (e as Error).message);
    } finally {
      cardLinking.value = false;
    }
  }, 200);

  const linkError = useDebounceFn(function (data: any) {
    console.error('Card link error', data);
    cardErrorMessage.value = t('signup.single-page.billing.error');
    cardError.value = true;
    emit('error', data);
  }, 200);

  onMounted(() => {
    document.addEventListener('change-it-link-card-form-success', linkSuccess);
    document.addEventListener('change-it-link-card-form-error', linkError);

    loadPlatformForm();
  });

  onUnmounted(() => {
    document.removeEventListener('change-it-link-card-form-success', linkSuccess);
    document.removeEventListener('change-it-link-card-form-error', linkError);
  });
</script>

<template>
  <div class="skeleton-container" v-if="loading">
    <v-skeleton-loader
      class="line-skeleton"
      type="image"
    />
    <div class="two-fields">
      <v-skeleton-loader
        class="line-skeleton"
        type="image"
        width="49%"
      />
      <v-skeleton-loader
        class="line-skeleton"
        type="image"
        width="49%"/>
    </div>
    <v-skeleton-loader
      class="image-skeleton"
      type="image"
    />
    <v-skeleton-loader
      class="image-skeleton"
      type="image"
    />
  </div>
  <div v-show="!loading" :id="bgElementId" class="content">

    <div v-if="cardSuccess" class="title content">
      <div class="t-title-1 t-bold">
        <img :src="cardSuccessImage" width="50" height="50" alt="">
      </div>
      <template v-if="subscriptionFlowStore.mode === 'subscribe'">
        <div class="t-body-1 mt-4 message">
          {{ $t('modal.link.card.membership_activated') }}
        </div>
        <div class="t-body-1 mt-4">
          {{ $t('modal.link.card.enjoy_benefits') }}
        </div>
      </template>
      <div v-else class="t-body-1 mt-4 message">
        {{ $t('subscription.flow.billing.checkout.card.success') }}
      </div>
    </div>
    <div v-else-if="cardLinking" class="card-link-loader">
      <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else-if="cardError" class="card-error">
      {{ cardErrorMessage }}
    </div>
    <div v-show="!cardLinking && !cardSuccess" :id="formElementId" ref="form" class="form"></div>
  </div>
</template>

<style scoped lang="scss">
  .message {
    font-weight: 700;
    font-size: 18px;
  }

  .line-skeleton {
    :deep(.v-skeleton-loader__image) {
      height: 40px;
    }
  }

  .skeleton-container {
    width: 100%;
    display: grid;
    gap: 10px;
  }

  .two-fields {
    display: flex;
    gap: 10px;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: calc(var(--inner-vh-full) - 300px);

    .form {
      width: 100%;
      height: 100%;
    }

    .card-link-loader {
      height: 100%;
      max-width: 900px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .card-error {
      width: 100%;
      max-width: 500px;
      justify-content: center;
      height: 3rem;
      display: flex;
      align-items: center;
      color: rgb(var(--v-theme-error));
      font-size: getFontSize('body-1');
      margin-bottom: 1.5rem;
    }

    .donation-cap-wrapper {
      :deep(.v-input__details) {
        display: none;
      }
    }
  }

  @media(max-width: breakpoint(md) - 1px) {
    .content {
      height: calc(var(--inner-vh-full) - 280px);
    }
  }
</style>
